<template>
  <hr-form-dialog
    ref="dialog"
    size="lg"
    maximize
    :maximized="maximized"
    :title="title"
    @update:maximized="$emit('update:maximized', $event)"
    @close="hide"
    @hide="onDialogHide"
    @submit="onSaveNoteTemplate"
  >
    <div class="row q-pt-none q-col-gutter-md">
      <hr-multi-select
        v-if="!isFormTemplate"
        v-model="selectSessionActivityTypes"
        hide-bottom-space
        data-e2e="select-activity-types"
        :options="availableSessionActivityTypes"
        :disable="offlineMode"
        :label="$t('sessions.noteTemplate.editSessionNoteTemplate.selectSessionActivityTypesLabel')"
        class="col-12"
      />
      <hr-text-field
        v-model="editSessionNoteTemplate.noteTemplateName"
        hide-bottom-space
        data-e2e="input-template-name"
        :disable="offlineMode"
        :rules="[
          val => !!val
            || $t('sessions.noteTemplate.editSessionNoteTemplate.inputTemplateNameValidator')
        ]"
        :label="$t('sessions.noteTemplate.editSessionNoteTemplate.inputTemplateNameLabel')"
        class="col-12"
      />
      <div
        class="col-12"
      >
        <text-editor
          ref="tiptapEditor"
          v-model="textEditorModel"
          tables
          image-upload
          enable-dynamic-fields
          enable-data-section
          checklist
          wrapper-class="q-my-none sticky-toolbar"
          :disabled="offlineMode"
          data-e2e="input-notes"
          @error-status-update="(status) => isMustacheError = status"
        >
          <text-editor-fields-toolbar
            :label="$t('common.buttonDynamicFields')"
            :add-button-label="$t('sessions.noteTemplate.editSessionNoteTemplate.btnAddField')"
            :field-categories="tokenFieldCategories"
            :is-loading="isLoadingCustomFields"
            indent
            @insert="onInsertToken"
            @edit="onEditField"
            @add="onAddNewField"
          />
        </text-editor>
      </div>
      <hr-expandable-section
        :label="$t('sessions.noteTemplate.editSessionNoteTemplate.headlineSignatures')"
        :default-opened="requireSignature || requireMoreSignatures"
        shape="lg"
        class="col-12"
      >
        <div class="col-12 row">
          <div class="col-12 hover-detect">
            <hr-checkbox
              v-model="requireSignature"
              data-e2e="checkbox-require-signature"
              :disable="offlineMode"
            >
              {{ $t('sessions.noteTemplate.editSessionNoteTemplate.checkboxRequireSignatureLabel') }}
              <hr-icon-hint-btn
                :hint="$t('sessions.noteTemplate.editSessionNoteTemplate.tooltipRequireSignature')"
                class="q-ml-sm show-on-hover"
                @click.stop
              />
            </hr-checkbox>
          </div>
          <div class="col-12 hover-detect">
            <hr-checkbox
              v-model="requireMoreSignatures"
              data-e2e="checkbox-require-more-signatures"
              :disable="!hasUserDataFields || offlineMode"
            >
              {{ $t('sessions.noteTemplate.editSessionNoteTemplate.checkboxRequireMoreSignaturesLabel') }}
              <hr-tooltip
                v-if="!hasUserDataFields"
              >
                {{ $t('sessions.noteTemplate.editSessionNoteTemplate.tooltipCannotRequireMoreSignatures') }}
              </hr-tooltip>
              <hr-icon-hint-btn
                :hint="$t('sessions.noteTemplate.editSessionNoteTemplate.tooltipRequireMoreSignatures')"
                class="q-ml-sm show-on-hover"
                @click.stop
              />
            </hr-checkbox>
          </div>
          <div class="col-12 row q-col-gutter-sm">
            <hr-multi-select
              v-if="requireMoreSignatures"
              v-model="additionalSignatories"
              data-e2e="select-additional-signatories"
              emit-object
              :options="combinedUserDataFields"
              :disable="offlineMode"
              option-value="id"
              :label="$t('sessions.noteTemplate.editSessionNoteTemplate.selectMoreSignatoriesLabel')"
              class="col-12"
            />
          </div>
          <div
            v-if="!isFormTemplate"
            class="col-12 hover-detect"
          >
            <hr-checkbox
              v-model="includeProfessionalCredentials"
              :disable="offlineMode"
            >
              {{ $t('sessions.noteTemplate.editSessionNoteTemplate.checkboxSignatoryCredentialsLabel') }}
              <hr-icon-hint-btn
                :hint="$t('sessions.noteTemplate.editSessionNoteTemplate.tooltipSignatoryCredentials')"
                class="q-ml-sm show-on-hover"
                @click.stop
              />
            </hr-checkbox>
          </div>
        </div>
      </hr-expandable-section>
      <hr-expandable-section
        :label="$t('sessions.noteTemplate.editSessionNoteTemplate.headlineContent')"
        :default-opened="lockContent"
        shape="lg"
        class="col-12"
      >
        <div class="col-12 row">
          <div class="col-12">
            <hr-checkbox
              v-model="lockContent"
              :disable="offlineMode"
              :label="$t('sessions.noteTemplate.editSessionNoteTemplate.checkboxLockContent')"
            />
          </div>
          <div class="col-12 hover-detect">
            <hr-checkbox
              v-if="!isFormTemplate"
              v-model="allowTimeChangeRequest"
              :disable="!lockContent"
            >
              {{ $t('sessions.noteTemplate.editSessionNoteTemplate.checkboxAllowTimeChangeRequest') }}
              <hr-tooltip
                v-if="!lockContent"
              >
                {{
                  $t('sessions.noteTemplate.editSessionNoteTemplate.changeRequestTooltips.allowTimeChangeRequest')
                }}
              </hr-tooltip>
              <hr-icon-hint-btn
                :hint="$t('sessions.noteTemplate.editSessionNoteTemplate.tooltipAllowTimeChangeRequest')"
                class="q-ml-sm show-on-hover"
                @click.stop
              />
            </hr-checkbox>
          </div>
        </div>
      </hr-expandable-section>
      <hr-expandable-section
        v-if="!isFormTemplate"
        :label="$t('sessions.noteTemplate.editSessionNoteTemplate.headlineNoteAuditor')"
        :default-opened="enableNoteAuditor"
        shape="lg"
        class="col-12"
      >
        <auditor-template-settings
          v-model:enabled="enableNoteAuditor"
          v-model:strictness="noteAuditorStrictness"
          class="col-12"
          :disable="offlineMode || waitingSpinner"
        />
      </hr-expandable-section>
      <hr-expandable-section
        v-if="!isFormTemplate"
        :label="$t('sessions.noteTemplate.editSessionNoteTemplate.headlineDataSegmentation')"
        :default-opened="splitDataByProvider"
        shape="lg"
        class="col-12"
      >
        <div
          class="col-12 row"
        >
          <hr-checkbox
            v-model="splitDataByProvider"
            :label="$t('sessions.noteTemplate.editSessionNoteTemplate.checkboxSplitDataByProvider')"
          />
        </div>
      </hr-expandable-section>
    </div>
    <template #actions>
      <hr-dismiss-btn
        :label="$t('common.buttonCancel')"
        @click="onCancelClick"
      />
      <hr-supporting-btn
        :label="$t('common.buttonPreview')"
        :disable="offlineMode"
        @click="onPreview"
      />
      <hr-confirm-btn
        type="submit"
        :disable="isSaveDisabled"
        :loading="waitingSpinner"
        data-e2e="button-save"
        :label="$t('common.buttonSave')"
      >
        <hr-tooltip
          v-if="!changeRequestTemplateHasAllRequiredFields"
        >
          {{
            // eslint-disable-next-line max-len
            $t( 'sessions.noteTemplate.editSessionNoteTemplate.changeRequestTooltips.timeChangeRequestMissingRequiredFields' ) }}
        </hr-tooltip>
      </hr-confirm-btn>
    </template>
  </hr-form-dialog>
</template>
<script>
import SelectClient from 'components/Clients/Modals/SelectClient';
import SyncedFieldsWarning from 'components/Modals/SyncedFieldsWarning.vue';
import EditSessionNoteField from 'components/Sessions/SessionNoteTemplates/EditSessionNoteField';
import SessionNoteFieldPrompt from 'components/Sessions/SessionNoteTemplates/SessionNoteFieldPrompt';
import TextEditor from 'components/UI/TextEditor';
import TextEditorFieldsToolbar from 'components/UI/TextEditorFieldsToolbar';
import {
  chain, concat, filter, find, flatMap, get, includes, isEmpty, map, set,
} from 'lodash';
import { mapActions, mapState } from 'pinia';
import { HUMANIZED_INTEGRATION_NAMES } from 'src/constants/integrations';
import {
  AuditorTemplateSettings, isAuditorEnabledForTemplate, resolveTemplateStrictness,
} from 'src/domains/noteAuditor';
import {
  HrCheckbox, HrConfirmBtn, HrDismissBtn, HrExpandableSection, HrFormDialog,
  HrIconHintBtn, HrMultiSelect, HrSupportingBtn, HrTextField, HrTooltip,
} from 'src/design-system';
import RealTimeNotificationsDB from 'src/fb/realTimeNotifications-db';
import SessionNoteFieldsDB from 'src/fb/sessionNoteFields-db';
import SessionNoteTemplatesDB from 'src/fb/sessionNoteTemplates-db';
import SessionsDB from 'src/fb/sessions-db';
import Interactions from 'src/mixins/Interactions';
import ReportImageUpload from 'src/mixins/ReportImageUpload';
import SessionActivitiesMixin from 'src/mixins/SessionActivitiesMixin';
import SessionNotesTemplates from 'src/mixins/SessionNotesTemplates';
import TemplateMixin from 'src/mixins/TemplateMixin';
import { auth } from 'src/store/auth';
import { clients } from 'src/store/clients';
import { dataFields } from 'src/store/dataFields';
import { network } from 'src/store/network';
import { programs } from 'src/store/programs';
import AppUtils from 'src/utils/AppUtils';
import { getIntegrationManagedFields } from 'src/utils/Clients';
import { logEvent } from 'src/utils/LogEvent';
import { replaceStorageImagesWithLocal } from 'src/utils/OfflineAccessUtils';

export default {
  name: 'EditSessionNoteTemplate',

  components: {
    AuditorTemplateSettings,
    TextEditor,
    TextEditorFieldsToolbar,
    HrConfirmBtn,
    HrDismissBtn,
    HrExpandableSection,
    HrFormDialog,
    HrIconHintBtn,
    HrSupportingBtn,
    HrTextField,
    HrTooltip,
    HrCheckbox,
    HrMultiSelect,
  },

  mixins: [Interactions, SessionNotesTemplates, TemplateMixin, ReportImageUpload, SessionActivitiesMixin],

  props: {
    sessionNoteTemplate: {
      type: Object,
      required: true,
    },
    isFormTemplate: {
      type: Boolean,
      default: false,
    },
    maximized: {
      type: Boolean,
      default: false,
    },
  },

  emits: ['hide', 'ok', 'update:maximized'],

  data() {
    return {
      waitingSpinner: false,
      isLoadingCustomFields: false,
      editTemplateText: '',
      offlineTemplateText: '',
      editSessionNoteTemplate: {
        id: null,
        noteTemplateName: '',

        templateText: '',
        fields: [],
      },
      isMustacheError: false,
      customSessionNoteFields: [],
    };
  },

  computed: {
    ...mapState(auth, ['userDetails', 'userSettings', 'integrationSettings']),
    ...mapState(network, ['networkOnline', 'offlineMode']),
    ...mapState(dataFields, ['clientDataFields', 'staffMemberDataFields']),
    ...mapState(programs, ['allPrograms']),

    title() {
      if (this.hasSessionNoteTemplateId) {
        return this.$t(`sessions.noteTemplate.${this.isFormTemplate
          ? 'editFormTemplate'
          : 'editSessionNoteTemplate'}.textTitleEdit`);
      }
      return this.$t(`sessions.noteTemplate.${this.isFormTemplate
        ? 'editFormTemplate'
        : 'editSessionNoteTemplate'}.textTitleAdd`);
    },

    hasSessionNoteTemplateId() {
      return (get(this.editSessionNoteTemplate, 'id', null) !== null);
    }, // hasSessionNoteTemplateId()

    hasUserDataFields() {
      return !isEmpty(this.clientDataUserFields) || !isEmpty(this.staffDataUserFields);
    },

    availableSessionActivityTypes() {
      return this.sessionActivityTypeOptions.filter(({ value }) => value !== 'form');
    },

    clientDataUserFields() {
      return filter(this.clientDataFields, { type: 'user' });
    },

    staffDataUserFields() {
      return this.staffMemberDataFields.filter(({ type }) => type === 'user');
    },

    combinedUserDataFields() {
      const clientSection = this.clientDataUserFields?.length ? [
        {
          subHeader: this.$t('settings.clientInformation.breadcrumbElLabel'),
          options: this.clientDataUserFields,
        },
      ] : [];
      const staffSection = this.staffDataUserFields?.length ? [
        {
          subHeader: this.$t('settings.staffMemberData.breadcrumbElLabel'),
          options: this.staffDataUserFields,
        },
      ] : [];
      return [...clientSection, ...staffSection];
    },

    fields() {
      return chain(this.editSessionNoteTemplate)
        .get('fields', [])
        .concat(this.customSessionNoteFields)
        .value();
    },

    tokenFieldCategories() {
      return chain(this.sessionNoteFieldPresets)
        .map((fieldPreset) => ({
          category: fieldPreset.category,
          token: fieldPreset.token,
          label: fieldPreset.label,
          tokenType: 'dynamicField',
          canEdit: false,
          type: 'preset',
          id: null,
          hiddenFromSelection: !!fieldPreset.hiddenFromSelection,
        }))
        .concat(
          map(this.fields, (field) => this.getTokenField(field)),
        )
        // Prevent null in groupBy as browser *can* handle it differently
        .groupBy('category')
        .map((fields) => this.sessionNoteFieldPresetToCategories(fields))
        .value();
    }, // tokenFieldCategories

    tokenFields() {
      return flatMap(this.tokenFieldCategories, 'fields');
    }, // tokenFields()

    categoryOptions() {
      return chain(this.tokenFieldCategories)
        .reject({ categoryIdentifier: null })
        .map((category) => ({
          value: category.categoryIdentifier,
          label: category.categoryLabel,
        }))
        .value();
    }, // categoryOptions()

    textEditorModel: {
      get() {
        return this.offlineMode ? this.offlineTemplateText : this.editTemplateText;
      },
      set(value) {
        if (this.offlineMode) {
          return;
        }

        this.editTemplateText = value;
      },
    }, // textEditorModel

    requireSignature: {
      get() {
        return get(this.editSessionNoteTemplate, 'requireSignature', false);
      },
      set(value) {
        this.editSessionNoteTemplate.requireSignature = value;
      },
    }, // requireSignature()

    requireMoreSignatures: {
      get() {
        return get(this.editSessionNoteTemplate, 'requireMoreSignatures', false);
      },
      set(value) {
        this.editSessionNoteTemplate.requireMoreSignatures = value;
      },
    },

    includeProfessionalCredentials: {
      get() {
        return get(this.editSessionNoteTemplate, 'includeProfessionalCredentials', false);
      },
      set(value) {
        this.editSessionNoteTemplate.includeProfessionalCredentials = value;
      },
    }, // includeProfessionalCredentials()

    // @todo Additional signatories should use map-options and emit-value instead of saving the whole clientData object
    additionalSignatories: {
      get() {
        return get(this.editSessionNoteTemplate, 'additionalSignatories', []);
      },
      set(value) {
        this.editSessionNoteTemplate.additionalSignatories = value;
      },
    },

    selectSessionActivityTypes: {
      get() {
        return get(this.editSessionNoteTemplate, 'sessionActivityTypes', []);
      },
      set(value) {
        this.editSessionNoteTemplate.sessionActivityTypes = value;
      },
    },

    enableNoteAuditor: {
      get() {
        return isAuditorEnabledForTemplate(this.editSessionNoteTemplate);
      },
      set(value) {
        this.editSessionNoteTemplate.enableNoteAuditor = value;
      },
    }, // enableNoteAuditor()

    noteAuditorStrictness: {
      get() {
        return resolveTemplateStrictness(this.editSessionNoteTemplate);
      },
      set(value) {
        this.editSessionNoteTemplate.noteAuditorStrictness = value;
      },
    }, // noteAuditorStrictness()

    lockContent: {
      get() {
        return get(this.editSessionNoteTemplate, 'lockContent', false);
      },
      set(value) {
        this.editSessionNoteTemplate.lockContent = value;

        if (!value) {
          this.editSessionNoteTemplate.allowTimeChangeRequest = false;
        }
      },
    },

    allowTimeChangeRequest: {
      get() {
        return get(this.editSessionNoteTemplate, 'allowTimeChangeRequest', false);
      },
      set(value) {
        this.editSessionNoteTemplate.allowTimeChangeRequest = value;
      },
    },

    splitDataByProvider: {
      get() {
        return this.editSessionNoteTemplate?.splitDataByProvider || false;
      },
      set(value) {
        this.editSessionNoteTemplate.splitDataByProvider = value;
      },
    },

    isSaveDisabled() {
      return !this.networkOnline || this.offlineMode || this.isMustacheError
        || !this.changeRequestTemplateHasAllRequiredFields;
    },

    changeRequestTemplateHasAllRequiredFields() {
      if (this.editSessionNoteTemplate.allowTimeChangeRequest) {
        return this.editTemplateText.includes('sessionStartTime')
        && this.editTemplateText.includes('sessionFinishTime');
      }
      return true;
    },

    syncWarningFields() {
      if (this.integrationSettings.isIntegrationSourceOfTruth) {
        const syncedFields = getIntegrationManagedFields(this.connectedClinic.integration);
        return this.fields?.filter(({ type, syncField, clientField, id, category }) => {
          return type === 'clientField'
            && syncField
            && syncedFields.includes(clientField)
            && this.editTemplateText.includes(`data-field="${category ? `${category}.${id}` : id}"`);
        });
      }
      return [];
    },
  },

  watch: {
    editTemplateText: {
      immediate: true,
      async handler(newVal) {
        if (!this.offlineMode) {
          return '';
        }

        this.offlineTemplateText = await replaceStorageImagesWithLocal(newVal);
      },
    },
  },

  methods: {
    ...mapActions(auth, ['changeCurrentClientById']),
    ...mapActions(clients, ['getStaffMemberByUser']),

    // following method is REQUIRED
    // (don't change its name --> "show")
    async show() {
      if (isEmpty(this.sessionNoteTemplate)) {
        this.editSessionNoteTemplate = {
          id: null,
          clinic: this.userDetails.clinic,
          noteTemplateName: '',
          templateText: '',
          requireSignature: false,
          fields: [],
        };
        this.editTemplateText = '';
      } else {
        this.editSessionNoteTemplate = { ...this.sessionNoteTemplate };
        this.editTemplateText = get(this.editSessionNoteTemplate, 'templateText');
      }

      if (this.isFormTemplate) {
        set(this.editSessionNoteTemplate, 'sessionActivityTypes', ['form']);
      }

      this.$refs.dialog.show();
      await this.loadSessionNoteFields();
      this.editTemplateText = this.convertTokensToHtml(this.tokenFields, this.editTemplateText);
    },

    // following method is REQUIRED
    // (don't change its name --> "hide")
    hide() {
      if (this.$refs.dialog) {
        this.$refs.dialog.hide();
      }
    },

    onDialogHide() {
      // required to be emitted
      // when QDialog emits "hide" event
      this.$emit('hide');
    },

    onShowDialogTooltip(tooltip) {
      this.$q.dialog({ message: tooltip });
    }, // onShowDialogTooltip()

    async onSaveNoteTemplate() {
      if (this.syncWarningFields.length) {
        const type = this.$t(`sessions.noteTemplate.${this.isFormTemplate ? 'form' : 'noteTemplate'}Type`);
        const integrationName = HUMANIZED_INTEGRATION_NAMES[this.connectedClinic.integration];
        const message = this.$t('clients.modals.editClient.dynamicFieldWarning', { type })
          + this.$t('clients.modals.editClient.pluralClientWarning', { integrationName, type });

        this.openComponentDialog(
          SyncedFieldsWarning,
          {
            title: this.$t('clients.modals.editClient.noDynamicFieldsSyncTitle'),
            message,
            secondaryMessage: this.$t('clients.modals.editClient.fieldsImpactedMessage'),
            fieldsList: this.syncWarningFields.map(({ label }) => label),
            okButton: this.$t('clients.modals.editClient.fieldsImpactedOkButton'),
          },
          async () => {
            await this.saveNoteTemplate();
            this.$emit('ok', this.savedTemplateResult());
            this.hide();
          },
        );
      } else {
        await this.saveNoteTemplate();
        // on OK, it is REQUIRED to
        // emit "ok" event (with optional payload)
        // before hiding the QDialog
        this.$emit('ok', this.savedTemplateResult());

        // then hiding dialog
        this.hide();
      }
    },

    onCancelClick() {
      // we just need to hide dialog
      this.hide();
    }, // onCancelClick()

    /**
     * @param {Field} tokenField
     */
    onInsertToken(tokenField) {
      this.$refs.tiptapEditor.insertDynamicField(
        this.getTokenHtml(tokenField.token, tokenField.label, tokenField.tokenType),
      );
    }, // onInsertToken()

    onAddNewField() {
      this.openComponentDialog(
        EditSessionNoteField,
        { categories: this.categoryOptions },
        async (field) => {
          if (field.category === null) {
            this.editSessionNoteTemplate.fields = chain(this.editSessionNoteTemplate)
              .get('fields', [])
              .reject({ id: field.id })
              .concat(field)
              .value();
          } else {
            this.loadSessionNoteFields();
          }

          this.onInsertToken(this.getTokenField(field));
        },
      );
    }, // onAddNewField()

    onEditField(sessionNoteFieldId) {
      const sessionNoteField = find(this.fields, { id: sessionNoteFieldId });
      this.openComponentDialog(
        EditSessionNoteField,
        {
          categories: this.categoryOptions,
          sessionNoteField,
        },
        async (field) => {
          if (field?.category === null) {
            this.editSessionNoteTemplate.fields = chain(this.editSessionNoteTemplate)
              .get('fields', [])
              .reject({ id: field.id })
              .concat(field)
              .value();
          } else {
            this.loadSessionNoteFields();
          }
        },
      );
    }, // onEditField()

    onPreview() {
      this.openComponentDialog(
        SelectClient,
        {
          title: this.$t('common.buttonPreview'),
          text: this.$t('sessions.noteTemplate.editSessionNoteField.textPreviewDialog'),
          okButton: this.$t('common.buttonPreview'),
          sessionSelector: true,
          sessionLabel: this.$t('sessions.noteTemplate.editSessionNoteField.labelSession'),
        },
        async ({ client, session }) => {
          if (isEmpty(client)) {
            return;
          }
          if (get(this.currentClient, 'id') !== client.id) {
            await this.changeCurrentClientById(client.id);
          }
          this.previewTemplate(session);
        },
      );
    }, // onPreview()

    async loadSessionNoteFields() {
      this.isLoadingCustomFields = true;
      try {
        const sessionNoteFieldsDb = new SessionNoteFieldsDB();
        this.customSessionNoteFields = await sessionNoteFieldsDb.readAll([['clinic', '==', this.userDetails.clinic]]);
      } finally {
        this.isLoadingCustomFields = false;
      }
    }, // loadSessionNoteFields()

    async saveNoteTemplate() {
      this.waitingSpinner = true;

      try {
        if (!this.editSessionNoteTemplate.id) {
          await this.createEmptyTemplate();
        }

        await this.prepareTemplateDataToBeSaved();
        await this.processSavingSessionNoteTemplate();

        await this.sendSignalNoteTemplateSaved();
      } catch (err) {
        logEvent(err);
        this.notifyNegative(err.message);
        throw err;
      } finally {
        this.waitingSpinner = false;
      }
    }, // saveNoteTemplate()

    async sendSignalNoteTemplateSaved() {
      const { offlineClients } = this.userSettings;
      if (!Array.isArray(offlineClients) || !offlineClients.length) {
        return;
      }

      const realTimeNotificationsDb = new RealTimeNotificationsDB();
      return realTimeNotificationsDb.create({
        clinic: this.connectedClinic.id,
        client: this.userSettings.offlineClients[0],
        context: RealTimeNotificationsDB.context.OFFLINE_MODE,
        isViewed: false,
        type: 'noteTemplateUpdated',
        viewedBy: [],
      });
    },

    async createEmptyTemplate() {
      const sessionNoteTemplatesDb = new SessionNoteTemplatesDB();
      const newSessionNotesTemplate = await sessionNoteTemplatesDb.create({
        ...this.editSessionNoteTemplate,
        templateText: '',
        fields: '',
      });
      this.editSessionNoteTemplate.id = newSessionNotesTemplate.id;
    }, // createEmptyTemplate()

    async prepareTemplateDataToBeSaved() {
      await this.processUploadingTemplateImagesInStorage();

      this.editSessionNoteTemplate.templateText = this.convertHtmlToTokens(this.tokenFields, this.editTemplateText);
      this.editSessionNoteTemplate.fields = chain(this.editSessionNoteTemplate)
        .get('fields', [])
        .filter((field) => (includes(
          this.editSessionNoteTemplate.templateText,
          `{{${field.id}}}`,
        )))
        .value();
    }, // prepareTemplateDataToBeSaved()

    /**
     * What the save produced. The id is read afterwards, because a new template has none until then.
     *
     * @return {{ sessionNoteTemplateId: string, enableNoteAuditor: boolean }}
     */
    savedTemplateResult() {
      return {
        sessionNoteTemplateId: this.editSessionNoteTemplate.id,
        enableNoteAuditor: isAuditorEnabledForTemplate(this.editSessionNoteTemplate),
      };
    }, // savedTemplateResult()

    async processSavingSessionNoteTemplate() {
      const sessionNoteTemplatesDb = new SessionNoteTemplatesDB();
      await sessionNoteTemplatesDb.set(
        this.editSessionNoteTemplate,
        this.editSessionNoteTemplate.id,
      );
    }, // createSessionNoteTemplate()

    async previewTemplate(sessionId) {
      const sessionsDb = new SessionsDB();
      const session = await sessionsDb.read(sessionId);

      const templateText = this.convertHtmlToTokens(this.tokenFields, this.editTemplateText);
      const previewSessionNoteTemplate = {
        ...this.editSessionNoteTemplate,
        templateText,
      };

      const templateFields = this.fetchTemplateFields(templateText);

      const sessionActivity = {
        startTimestamp: session.runTimestamp,
        endTimestamp: session.finishTimestamp,
      };

      const staffMember = await this.getStaffMemberByUser(this.userDetails.userId);

      const sessionNoteFieldData = this.compileSessionNoteInformation(
        session,
        this.currentClient,
        sessionActivity,
        null,
        staffMember,
      );

      this.openComponentDialog(
        SessionNoteFieldPrompt,
        {
          sessionNoteTemplate: previewSessionNoteTemplate,
          templateFields,
          sessionNoteFieldData,
          session,
          sessionActivity,
          allPrograms: this.allPrograms,
          alwaysAllowNoteCompletion: true,
          preview: true,

          client: this.currentClient,
        },
        null,
        null,
        null,
        { persistent: true },
      );
    },

    async processUploadingTemplateImagesInStorage() {
      await this.uploadAndEmbedImagesInTemplateText();
      await this.updateStorageImagesMetadata();
    }, // processSavingNotesImagesInStorage()

    async uploadAndEmbedImagesInTemplateText() {
      this.editTemplateText = await this.replaceImageSources(this.editTemplateText, {
        collectionName: 'sessionNoteTemplates',
        itemId: this.editSessionNoteTemplate.id,
      });
    }, // uploadAndEmbedImagesInTemplateText()

    async updateStorageImagesMetadata() {
      const originalNotes = this.editSessionNoteTemplate.templateText;
      const {
        imagesToDeletePaths,
        newImagesPaths,
      } = this.findImagesPathsToDeleteAndUpdate(originalNotes, this.editTemplateText);

      const deleteActions = map(
        imagesToDeletePaths,
        (path) => this.setImageFileMetadata(path, { [this.editSessionNoteTemplate.id]: null }),
      );
      const updateActions = map(
        newImagesPaths,
        (path) => this.setImageFileMetadata(path, { [this.editSessionNoteTemplate.id]: 'sessionNoteTemplates' }),
      );

      await AppUtils.allSettled(concat(deleteActions, updateActions), (err) => (
        this.notifyNegative(get(err, 'message', err))
      ));

      await this.deleteUnusedImagesAndClearDeleteQueue();
    }, // updateStorageImagesMetadata()
  },
};
</script>
<style lang="scss" scoped>

.hover-detect .show-on-hover {
  visibility: hidden;
}

.hover-detect:hover .show-on-hover {
  visibility: visible;
}

// Always show on mobile/touch devices
@media (hover: none) and (pointer: coarse) {
  .hover-detect .show-on-hover {
    visibility: visible !important;
  }
}

</style>
